import React, { useState, useEffect } from 'react';

// Pitch layout configurations
const PITCH_LAYOUTS = {
  FULL: { 
    sections: 8, 
    labels: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'], 
    name: 'Full Pitch',
    description: 'Standard 8-section layout for full-size pitches',
    gridCols: 2,
    gridRows: 4,
    ageGroups: ['U14+', 'Senior']
  },
  HALF: { 
    sections: 4, 
    labels: ['A', 'B', 'C', 'D'], 
    name: 'Half Pitch',
    description: 'U10-13 size pitch with 4 sections',
    gridCols: 2,
    gridRows: 2,
    ageGroups: ['U10', 'U11', 'U12', 'U13']
  },
  QUARTER: { 
    sections: 2, 
    labels: ['A', 'B'], 
    name: 'Quarter Pitch',
    description: 'U6-7 size pitch with 2 sections',
    gridCols: 1,
    gridRows: 2,
    ageGroups: ['U6', 'U7', 'U8', 'U9']
  },
  SINGLE: { 
    sections: 1, 
    labels: ['A'], 
    name: 'Single Space',
    description: 'Custom small space or training area',
    gridCols: 1,
    gridRows: 1,
    ageGroups: ['Training', 'Multi-use']
  }
};

const PitchSectionConfigModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onAdvanceToNext,  // NEW PROP
  pitchId, 
  currentConfig = null,
  pitchName = `Pitch ${pitchId}`,
  // Multi-pitch workflow props
  allPitchNames = [], 
  currentPitchIndex = 0, 
  totalPitches = 1 
}) => {
  const [selectedLayout, setSelectedLayout] = useState(currentConfig?.sectionLayout || 'FULL');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Reset form when modal opens with new data or pitch changes
  useEffect(() => {
    if (isOpen) {
      setSelectedLayout(currentConfig?.sectionLayout || 'FULL');
      setShowSuccessPopup(false);
    }
  }, [isOpen, currentConfig, pitchId, currentPitchIndex]);

  const isMultiPitch = totalPitches > 1;
  const isLastPitch = currentPitchIndex === totalPitches - 1;
  const remainingPitches = totalPitches - currentPitchIndex - 1;

  const handleSave = async () => {
    if (!selectedLayout) return;
    
    setIsSaving(true);
    
    try {
      const config = {
        pitchId,
        sectionLayout: selectedLayout,
        customName: pitchName,
        sectionsCount: PITCH_LAYOUTS[selectedLayout].sections,
        sectionLabels: PITCH_LAYOUTS[selectedLayout].labels,
        layoutName: PITCH_LAYOUTS[selectedLayout].name,
        updatedAt: new Date().toISOString()
      };
      
      // Call onSave and let parent handle state updates
      await onSave(config);
      
      // Show success popup for multi-pitch workflow
      if (isMultiPitch) {
        setShowSuccessPopup(true);
      } else {
        // Single pitch only - close modal immediately
        onClose();
      }
    } catch (error) {
      console.error('Error saving pitch configuration:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    
    if (isLastPitch) {
      // Last pitch - tell parent to complete the workflow
      if (onAdvanceToNext) {
        onAdvanceToNext();
      } else {
        onClose();
      }
    } else {
      // Not last pitch - tell parent to advance to next pitch
      if (onAdvanceToNext) {
        onAdvanceToNext();
      }
    }
  };

  const handleClose = () => {
    if (!isSaving && !showSuccessPopup) {
      onClose();
    }
  };

  // Render section preview
  const renderSectionPreview = (layout) => {
    const config = PITCH_LAYOUTS[layout];
    
    return (
      <div style={{
        width: '120px',
        height: '160px',
        backgroundColor: '#dcfce7',
        border: '2px solid white',
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
        margin: '0 auto'
      }}>
        {/* Pitch background */}
        <div style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          right: '4px',
          bottom: '4px',
          backgroundColor: '#bbf7d0',
          borderRadius: '4px',
          border: '1px solid white'
        }}>
          {/* Center line */}
          <div style={{
            position: 'absolute',
            left: '0',
            right: '0',
            top: '50%',
            height: '1px',
            backgroundColor: 'white',
            transform: 'translateY(-50%)'
          }}></div>
          
          {/* Sections grid */}
          <div style={{
            position: 'absolute',
            top: '2px',
            left: '2px',
            right: '2px',
            bottom: '2px',
            display: 'grid',
            gridTemplateColumns: `repeat(${config.gridCols}, 1fr)`,
            gridTemplateRows: `repeat(${config.gridRows}, 1fr)`,
            gap: '1px'
          }}>
            {config.labels.map((label, index) => (
              <div
                key={label}
                style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.3)',
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#1e40af',
                  borderRadius: '2px'
                }}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal - Always show when not showing success popup */}
      {!showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)'
          }}>
            {/* Header with progress */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 4px 0'
                }}>
                  Configure {pitchName}
                </h2>
                {isMultiPitch && (
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 8px 0'
                  }}>
                    Pitch {currentPitchIndex + 1} of {totalPitches}
                  </p>
                )}
                
                {/* Progress bar for many pitches */}
                {totalPitches > 3 && (
                  <div style={{
                    marginTop: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${((currentPitchIndex + 1) / totalPitches) * 100}%`,
                        height: '100%',
                        backgroundColor: '#3b82f6',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                  </div>
                )}
                
                {/* Show all pitches list only if more than 1 pitch and <= 5 */}
                {isMultiPitch && allPitchNames.length > 0 && totalPitches <= 5 && (
                  <div style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '12px',
                    marginTop: '12px'
                  }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Configuration Queue
                    </label>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b'
                    }}>
                      {allPitchNames.map((name, index) => (
                        <div 
                          key={index}
                          style={{
                            padding: '4px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <span style={{
                            color: index < currentPitchIndex 
                              ? '#10b981' 
                              : index === currentPitchIndex 
                                ? '#3b82f6' 
                                : '#94a3b8'
                          }}>
                            {index < currentPitchIndex 
                              ? '✓' 
                              : index === currentPitchIndex 
                                ? '●' 
                                : '○'}
                          </span>
                          <span style={{
                            fontWeight: index === currentPitchIndex ? '600' : '400',
                            color: index === currentPitchIndex ? '#1f2937' : '#64748b'
                          }}>
                            {name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleClose}
                disabled={isSaving}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  opacity: isSaving ? 0.5 : 1,
                  marginLeft: '16px'
                }}
              >
                ×
              </button>
            </div>

            {/* Layout Selection */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '16px',
                margin: '0 0 16px 0'
              }}>
                Choose Section Layout
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px'
              }}>
                {Object.entries(PITCH_LAYOUTS).map(([key, layout]) => (
                  <div
                    key={key}
                    onClick={() => !isSaving && setSelectedLayout(key)}
                    style={{
                      border: '2px solid',
                      borderColor: selectedLayout === key ? '#3b82f6' : '#e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      backgroundColor: selectedLayout === key ? '#eff6ff' : 'white',
                      transition: 'all 0.2s',
                      opacity: isSaving ? 0.7 : 1
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginBottom: '12px'
                    }}>
                      {/* Radio button */}
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid',
                        borderColor: selectedLayout === key ? '#3b82f6' : '#d1d5db',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        flexShrink: 0
                      }}>
                        {selectedLayout === key && (
                          <div style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: '#3b82f6',
                            borderRadius: '50%'
                          }}></div>
                        )}
                      </div>
                      
                      {/* Layout info */}
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: '0 0 2px 0'
                        }}>
                          {layout.name}
                        </h4>
                        <p style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          margin: '0 0 4px 0'
                        }}>
                          {layout.description}
                        </p>
                        <div style={{
                          fontSize: '11px',
                          color: '#9ca3af'
                        }}>
                          {layout.sections} section{layout.sections !== 1 ? 's' : ''} • {layout.ageGroups.join(', ')}
                        </div>
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '12px'
                    }}>
                      {renderSectionPreview(key)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Layout Summary */}
            {selectedLayout && (
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0369a1',
                  margin: '0 0 8px 0'
                }}>
                  Configuration Summary
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '12px',
                  fontSize: '12px',
                  color: '#0c4a6e'
                }}>
                  <div>
                    <strong>Sections:</strong> {PITCH_LAYOUTS[selectedLayout].sections}
                  </div>
                  <div>
                    <strong>Labels:</strong> {PITCH_LAYOUTS[selectedLayout].labels.join(', ')}
                  </div>
                  <div>
                    <strong>Layout:</strong> {PITCH_LAYOUTS[selectedLayout].name}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={handleClose}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  color: '#374151',
                  opacity: isSaving ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !selectedLayout}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isSaving || !selectedLayout ? '#9ca3af' : '#3b82f6',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: isSaving || !selectedLayout ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontWeight: '500',
                  opacity: isSaving ? 0.7 : 1
                }}
              >
                {isSaving 
                  ? 'Saving...' 
                  : (isMultiPitch && !isLastPitch)
                    ? 'Configure Next Pitch'
                    : 'Save Configuration'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup - Show for multi-pitch workflow */}
      {showSuccessPopup && isMultiPitch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            margin: '20px',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.25)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '24px',
              color: 'white'
            }}>
              ✓
            </div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 8px 0'
            }}>
              {isLastPitch ? 'All Pitches Configured!' : `Pitch ${currentPitchIndex + 1} Saved`}
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: '0 0 24px 0'
            }}>
              {isLastPitch 
                ? `Successfully configured all ${totalPitches} ${totalPitches === 1 ? 'pitch' : 'pitches'} with section layouts.`
                : `Section layout configured successfully. ${remainingPitches} more ${remainingPitches === 1 ? 'pitch' : 'pitches'} remaining.`
              }
            </p>
            <button
              onClick={handleSuccessPopupClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {isLastPitch ? 'Complete' : 'Continue'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PitchSectionConfigModal;
